import { ApolloError, isApolloError } from 'apollo-client';

export interface GraphQLErrorExtension {
  name: string;
}

/**
 * This GraphQLError class is similar to the GraphQLError found within
 * the graphql npm package; it just only includes the `extensions` property.
 * We're not using a real GraphQL server so we don't need all the other
 * properties provided by that class that delve into the GraphQL document
 * AST in order to inform you where in the document your error originated.
 *
 * We do follow the same structure, however, by using the `extensions` object
 * as a dumping ground for passing useful error data back up from the resolvers
 * to the component code when something goes wrong.
 */
export class GraphQLError<
  E extends GraphQLErrorExtension = GraphQLErrorExtension
> extends Error {
  extensions: E;

  constructor(errorMessage: string, extensions: E) {
    super(errorMessage);
    this.extensions = extensions;
  }
}

interface ApolloGraphQLNetworkError<E extends GraphQLErrorExtension>
  extends ApolloError {
  networkError: GraphQLError<E>;
}

/**
 * Distinguishes between a regular Error and a GraphQLError by checking
 * for the `extensions` property on the Error instance
 */
const isGraphQLError = (error: Error | null): error is GraphQLError =>
  error && Object.hasOwnProperty.call(error, 'extensions') ? true : false;

/**
 * Create a TS TypeGuard function to check an ApolloError to see if it contains
 * a specific type of GraphQLError.
 */
export const createApolloErrorTypeGuard = <E extends GraphQLErrorExtension>(
  name: E['name'],
) => {
  return (
    error?: ApolloError | Error | null,
  ): error is ApolloGraphQLNetworkError<E> => {
    if (error && isApolloError(error)) {
      if (error.networkError && isGraphQLError(error.networkError)) {
        return error.networkError.extensions.name === name;
      }
    }
    return false;
  };
};
